import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { endpoints } from '../../../api/api';
import { Lead, SalesStatus, ContactHistoryEntry, SalesLeadsResponse } from '../types';
import {
  SALES_STATUS_OPTIONS, SALES_STATUS_LABELS,
  SALES_STATUS_BADGE_STYLES, SALES_STATUS_CHIP_STYLES,
  stepLabel,
} from '../stepLabels';
import { relativeTime } from '../utils';

interface ContactStatusModalProps {
  lead: Lead;
  onClose: () => void;
  onSaved: () => void;
  onSwitchLead: (lead: Lead) => void;
}

const statusChoices = SALES_STATUS_OPTIONS.filter(opt => opt.value);

const ContactStatusModal: React.FC<ContactStatusModalProps> = ({ lead, onClose, onSaved, onSwitchLead }) => {
  const [status, setStatus] = useState<SalesStatus>(lead.salesStatus);
  const [note, setNote] = useState(lead.salesNote || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<ContactHistoryEntry[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [personBooks, setPersonBooks] = useState<Lead[]>([]);
  const [personBooksLoading, setPersonBooksLoading] = useState(true);
  const [justSaved, setJustSaved] = useState(false);

  const loadHistory = async (bookId: string) => {
    try {
      const res = await axios.get(endpoints.getSalesContactHistory.replace(':bookId', bookId));
      setHistory(res.data.history || []);
    } catch { /* history is supplementary — a failed fetch shouldn't block editing */ }
  };

  // Status/notes are per-book, but a rep still needs to see this person's
  // full set of books — including the one currently open — and whether
  // anyone's already reached out about each.
  const loadPersonBooks = async (userId: string) => {
    try {
      const res = await axios.get<SalesLeadsResponse>(endpoints.getSalesLeads, { params: { userId, limit: 50 } });
      setPersonBooks(res.data.leads || []);
    } catch { /* supplementary — a failed fetch shouldn't block editing */ }
  };

  // No `key`-based remount here — switching between this person's own books
  // (via the sidebar) should feel like navigating within the same dialog,
  // not tearing the whole thing down and rebuilding it (which flickered).
  // So each piece of per-book state is synced explicitly instead.
  useEffect(() => {
    setStatus(lead.salesStatus);
    setNote(lead.salesNote || '');
    setError(null);
    setJustSaved(false);
  }, [lead.bookId, lead.salesStatus, lead.salesNote]);

  useEffect(() => {
    let cancelled = false;
    setHistoryLoading(true);
    loadHistory(lead.bookId).finally(() => { if (!cancelled) setHistoryLoading(false); });
    return () => { cancelled = true; };
  }, [lead.bookId]);

  // Deliberately keyed on userId only, not bookId — the sidebar list is the
  // same set of books for every book belonging to this person, so switching
  // between them shouldn't re-trigger a loading flash for data we already have.
  useEffect(() => {
    if (!lead.userId) { setPersonBooksLoading(false); return; }
    let cancelled = false;
    setPersonBooksLoading(true);
    loadPersonBooks(lead.userId).finally(() => { if (!cancelled) setPersonBooksLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead.userId]);

  // Stays open on save — the rep can see the history/sidebar update in place
  // and jump straight to another of this person's books rather than
  // re-opening the modal from the table each time.
  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setJustSaved(false);
    try {
      await axios.patch(endpoints.updateSalesContact.replace(':bookId', lead.bookId), { status, note });
      onSaved();
      await Promise.all([
        loadHistory(lead.bookId),
        lead.userId ? loadPersonBooks(lead.userId) : Promise.resolve(),
      ]);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2500);
    } catch (err) {
      setError('Failed to save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-3xl max-h-[85vh] flex flex-col">
        <div className="px-6 pt-6 pb-4 border-b border-gray-100 flex items-start justify-between gap-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{lead.userName || 'Lead'}</h3>
            {lead.phoneNumber ? (
              <a href={`tel:${lead.phoneNumber}`} className="text-sm text-blue-600 hover:underline">
                {lead.phoneNumber}
              </a>
            ) : (
              <p className="text-sm text-gray-400">No phone number on file</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[220px_minmax(0,1fr)] gap-6 px-6 py-4 overflow-y-auto">
          <div>
            <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Their books</h4>
            {!lead.userId ? (
              <p className="text-xs text-gray-400">No linked user account.</p>
            ) : personBooksLoading ? (
              <p className="text-xs text-gray-400">Loading...</p>
            ) : (
              <ul className="space-y-1">
                {personBooks.map((book) => {
                  const isCurrent = book.bookId === lead.bookId;
                  return (
                    <li key={book.bookId}>
                      <button
                        onClick={() => !isCurrent && onSwitchLead(book)}
                        disabled={isCurrent}
                        className={`w-full text-left text-xs p-2 rounded-md border ${
                          isCurrent
                            ? 'border-gray-900 bg-gray-50 cursor-default'
                            : 'border-transparent hover:bg-gray-50'
                        }`}
                      >
                        <p className="font-medium text-gray-800">{book.bookTitle || 'Untitled'}</p>
                        <p className="text-gray-400">{stepLabel(book.currentStep)}</p>
                        <span className={`inline-block mt-1 px-2 py-0.5 rounded-full font-medium ${SALES_STATUS_BADGE_STYLES[book.salesStatus]}`}>
                          {SALES_STATUS_LABELS[book.salesStatus]}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div>
            <p className="text-xs text-gray-400 mb-3">
              Editing "{lead.bookTitle}" — last contacted about this book {relativeTime(lead.salesUpdatedAt)}
            </p>

            <label className="block text-sm font-medium text-gray-700 mb-2">Contact status</label>
            <div className="flex flex-wrap gap-2 mb-4">
              {statusChoices.map(opt => {
                const selected = status === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setStatus(opt.value as SalesStatus)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${
                      selected
                        ? SALES_STATUS_CHIP_STYLES[opt.value].selected
                        : SALES_STATUS_CHIP_STYLES[opt.value].unselected
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>

            <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              maxLength={1000}
              rows={4}
              className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="e.g. Called on Monday, interested but wants a discount"
            />

            {error && <p className="text-red-500 text-sm mt-3">{error}</p>}

            <div className="mt-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Outreach history for this book</h4>
              {historyLoading ? (
                <p className="text-xs text-gray-400">Loading...</p>
              ) : history.length === 0 ? (
                <p className="text-xs text-gray-400">No previous outreach logged for this book.</p>
              ) : (
                <ul className="space-y-2 max-h-32 overflow-y-auto border border-gray-100 rounded-lg p-3">
                  {history.map((entry, idx) => (
                    <li key={idx} className="text-xs">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-700">{SALES_STATUS_LABELS[entry.status]}</span>
                        <span className="text-gray-400">{relativeTime(entry.createdAt)}</span>
                      </div>
                      {entry.note && <p className="text-gray-500 mt-0.5">{entry.note}</p>}
                      <p className="text-gray-300 mt-0.5">by {entry.updatedByName}</p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
          {justSaved && <span className="text-sm text-green-600">Saved ✓</span>}
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
          >
            Close
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm bg-gray-900 text-white rounded-lg hover:bg-gray-800 disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ContactStatusModal;
